import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowRight, Phone, Play, Check, ChevronDown,
  ShieldCheck, Award, Truck, Crosshair, ThumbsUp, Users, Star,
} from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import QuoteRequestModal from '../components/site/QuoteRequestModal';
import { useSeo } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIDEO_THUMB = 'https://images.pexels.com/photos/11950157/pexels-photo-11950157.jpeg';
const COMMIT_IMG = 'https://images.unsplash.com/photo-1651084296894-105edab05b26';

const FEATURES = [
  { icon: Users, title: 'Experienced Professionals', desc: 'Certified technicians with years of hands-on experience installing auto glass on every make and model.' },
  { icon: Award, title: 'OEM Quality Glass', desc: 'We install OEM or OEM-equivalent glass paired with premium adhesives for a factory-perfect finish.' },
  { icon: Truck, title: 'Mobile Convenience', desc: 'We come to your home, workplace or anywhere in Ottawa \u2013 fast, safe and hassle-free.' },
  { icon: Crosshair, title: 'ADAS Calibration Experts', desc: 'Factory-compliant camera and sensor calibration using professional-grade equipment.' },
  { icon: ShieldCheck, title: 'Insurance Approved', desc: 'We work directly with all major insurance companies and handle the paperwork for you.' },
  { icon: ThumbsUp, title: 'Customer First', desc: 'Fast communication, transparent pricing and outstanding reviews from every corner of Ottawa.' },
];

const COMMITMENT = [
  ['Safety First', 'We follow strict OEM procedures on every installation.'],
  ['Honest Pricing', 'Upfront quotes with no hidden fees or surprise charges.'],
  ['Fast Turnaround', 'Most windshields installed and calibrated within a day.'],
  ['Lifetime Workmanship Warranty', 'We stand behind every job for as long as you own the vehicle.'],
  ['OEM Procedures', 'Factory-approved adhesives, primers and calibration.'],
  ['Satisfaction Guaranteed', 'If it\u2019s not right, we make it right \u2013 no questions asked.'],
];

const STATS = [
  { value: 25000, suffix: '+', label: 'Windshields Installed' },
  { value: 4.9, suffix: '\u2605', label: 'Google Rating', decimal: 1 },
  { value: 20, suffix: '+', label: 'Years Experience' },
  { value: 100, suffix: '%', label: 'Customer Satisfaction' },
];

const TESTIMONIALS = [
  { name: 'Sarah M.', location: 'Ottawa, ON', rating: 5,
    text: 'Booked in the morning, van arrived by lunch. The tech was courteous, the calibration was perfect, and my insurance was handled without me lifting a finger. Best experience I\u2019ve had with a service company.' },
  { name: 'David R.', location: 'Kanata, ON', rating: 5,
    text: 'A+ replaced the windshield on my Tesla Model Y and re-calibrated the cameras flawlessly. Prices were fair, quality was OEM, and I was back on the road the same day.' },
  { name: 'Priya K.', location: 'Orleans, ON', rating: 5,
    text: 'They came to my office, replaced my windshield in about an hour, and left the car spotless. Truly the A+ difference \u2013 highly recommend.' },
];

const FAQS = [
  { q: 'How long does windshield replacement take?', a: 'A typical replacement takes 60\u201390 minutes. If your vehicle requires ADAS calibration, plan for an extra 30\u201360 minutes.' },
  { q: 'Do you work with insurance?', a: 'Yes \u2013 we\u2019re approved with every major insurance provider in Ontario and handle claims and paperwork directly for you.' },
  { q: 'Can you calibrate my ADAS system?', a: 'Absolutely. We use factory-approved calibration targets and tooling for every modern camera and sensor system.' },
  { q: 'Do you offer mobile service?', a: 'Yes. Our fully-equipped mobile units can come to your home or office anywhere in Ottawa and the surrounding area.' },
  { q: 'Is OEM glass available?', a: 'Yes. On request we can source true OEM glass; otherwise we install OEM-equivalent glass that meets or exceeds original spec.' },
  { q: 'Do you guarantee your work?', a: 'Every installation is covered by our lifetime workmanship warranty for as long as you own the vehicle.' },
];

// ---------- Animated counter ----------
function useOnScreen(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

function Counter({ value, suffix = '', decimal = 0 }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const dur = 1600;
    const startTime = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - startTime) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (value - start) * eased);
      if (p < 1) requestAnimationFrame(step);
      else setDisplay(value);
    };
    requestAnimationFrame(step);
  }, [visible, value]);
  const formatted = decimal > 0 ? display.toFixed(decimal) : Math.floor(display).toLocaleString();
  return <span ref={ref}>{formatted}{suffix}</span>;
}

// ---------- Fade-in on scroll ----------
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >{children}</div>
  );
}

// ---------- FAQ Item ----------
function FaqItem({ q, a, isOpen, onToggle }) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left px-6 py-5 hover:bg-slate-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-[15px] font-semibold text-[#111827] pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#0D5BD7] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-5 text-[14px] text-[#5F6773] leading-[1.65]">{a}</div>
        </div>
      </div>
    </div>
  );
}

// ================== PAGE ==================
export default function WhyChooseUs() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [google, setGoogle] = useState(null); // { rating, total_reviews, maps_url, reviews }

  useSeo({
    title: 'Why Choose A+ Auto Glass — Ottawa Windshield & ADAS Experts',
    description:
      'OEM-quality glass, certified ADAS calibration, mobile service and a lifetime workmanship warranty. See why Ottawa drivers trust A+ Auto Glass for windshield replacement.',
    path: '/why-choose-us',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const handler = () => setQuoteOpen(true);
    window.addEventListener('open-quote-modal', handler);
    // Fetch live Google reviews
    axios.get(`${API}/public/google-reviews`)
      .then((r) => { if (r.data?.available) setGoogle(r.data); })
      .catch(() => {});
    return () => window.removeEventListener('open-quote-modal', handler);
  }, []);

  // Real Google rating + review count, fall back to sensible defaults if API unavailable
  const googleRating = google?.rating || 4.9;
  const googleReviewCount = google?.total_reviews || 0;
  const googleReviews = (google?.reviews || []).slice(0, 3);
  const useLiveReviews = googleReviews.length > 0;

  const dynamicStats = [
    { value: 3000, suffix: '+', label: 'Windshields Installed' },
    { value: googleRating, suffix: '\u2605', label: 'Google Rating', decimal: 1 },
    { value: 20, suffix: '+', label: 'Years Experience' },
    { value: 100, suffix: '%', label: 'Customer Satisfaction' },
  ];

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <Navbar />

      {/* HERO (desktop only) — matches Services / Locations / Contact */}
      <section className="hidden lg:block relative overflow-hidden bg-[#0a1220] text-white">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#0D5BD7]/20 to-transparent pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-14">
          <h1 className="font-black leading-[1.05] tracking-[-0.015em] text-[32px] md:text-[42px] lg:text-[48px] max-w-[680px]">
            Why Choose A+ Auto Glass
          </h1>
          <p className="mt-4 text-[15px] md:text-[16.5px] text-white/85 max-w-[600px] leading-[1.65]">
            Premium auto glass replacement, ADAS calibration and mobile service &mdash; delivered with
            exceptional care across Ottawa and the surrounding area.
          </p>
        </div>
      </section>

      {/* WHY CHOOSE - 6 CARDS */}
      <section className="bg-white pt-12 pb-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">WHY CHOOSE</div>
            <h2 className="mt-3 text-[34px] md:text-[46px] font-black text-[#111827] tracking-[-0.01em] leading-[1.05]">
              Why Choose A+ Auto Glass?
            </h2>
            <p className="mt-5 text-[15px] text-[#5F6773] leading-[1.7]">
              We combine certified technicians, OEM-quality materials, advanced ADAS calibration equipment, and outstanding customer service to provide the best auto glass experience possible.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 80}>
                <div className="group h-full bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] p-8 hover:shadow-[0_18px_40px_rgba(13,91,215,0.12)] hover:-translate-y-1 transition-all duration-300">
                  <div className="w-14 h-14 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                    <f.icon className="w-6 h-6" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-5 text-[17px] font-bold text-[#111827] tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-[14px] text-[#5F6773] leading-[1.65]">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK - 40/60 split */}
      <section className="bg-[#F8F9FB] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-2">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">HOW WE WORK</div>
            <h2 className="mt-3 text-[32px] md:text-[42px] font-black text-[#111827] tracking-[-0.01em] leading-[1.05]">
              See the A+ Difference
            </h2>
            <p className="mt-5 text-[15px] text-[#5F6773] leading-[1.75]">
              Take a look behind the scenes and see how our team professionally removes, cleans and installs your windshield using OEM-approved adhesives, precise ADAS calibration, and meticulous attention to detail on every job.
            </p>
            <button
              onClick={() => setVideoOpen(true)}
              className="group mt-8 inline-flex items-center h-12 px-6 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[12px] font-bold tracking-[0.22em] transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px]"
            >
              <Play className="w-4 h-4 mr-3 fill-white" /> WATCH VIDEO
            </button>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={150}>
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className="group relative w-full aspect-video rounded-2xl overflow-hidden shadow-xl block"
            >
              <img src={VIDEO_THUMB} alt="Watch how A+ Auto Glass replaces a windshield" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-[#0D5BD7] ml-1 fill-[#0D5BD7]" />
                </div>
              </div>
            </button>
          </Reveal>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img src={COMMIT_IMG} alt="Technician installing a windshield" className="w-full h-[420px] lg:h-[520px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">OUR COMMITMENT</div>
            <h2 className="mt-3 text-[32px] md:text-[42px] font-black text-[#111827] tracking-[-0.01em] leading-[1.05]">
              Your Safety Is Our Priority
            </h2>
            <p className="mt-5 text-[15px] text-[#5F6773] leading-[1.75] max-w-lg">
              Auto glass is more than replacing glass — it&apos;s restoring the structural safety of your vehicle. Every installation is performed to strict industry and OEM standards so you drive away with confidence.
            </p>
            <ul className="mt-7 space-y-4">
              {COMMITMENT.map(([title, desc], i) => (
                <li key={title} className="flex items-start gap-4 group">
                  <span className="mt-0.5 w-8 h-8 rounded-full bg-[#0D5BD7]/10 text-[#0D5BD7] flex items-center justify-center shrink-0 group-hover:bg-[#0D5BD7] group-hover:text-white transition-colors">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </span>
                  <div>
                    <div className="font-semibold text-[#111827] text-[15px]">{title}</div>
                    <div className="text-[13.5px] text-[#5F6773] leading-[1.6]">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* STATS COUNTERS */}
      <section className="bg-[#0a1a2f] text-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {dynamicStats.map((s, i) => (
            <Reveal key={s.label} delay={i * 100}>
              <div>
                <div className="text-[44px] md:text-[56px] font-black tracking-tight text-white">
                  <Counter value={s.value} suffix={s.suffix} decimal={s.decimal || 0} />
                </div>
                <div className="mt-2 text-[11px] tracking-[0.22em] font-semibold text-[#7fb0ff] uppercase">
                  {s.label}
                </div>
                {s.label === 'Google Rating' && googleReviewCount > 0 && (
                  <div className="mt-1 text-[11px] text-white/60">
                    {`from ${googleReviewCount} Google reviews`}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">REVIEWS</div>
            <h2 className="mt-3 text-[32px] md:text-[44px] font-black text-[#111827] tracking-[-0.01em] leading-[1.05]">
              What Our Customers Say
            </h2>
            {useLiveReviews && (
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#5F6773]">
                <span className="inline-flex items-center gap-1 text-[#0D5BD7] font-bold">
                  {googleRating.toFixed(1)}
                  <Star className="w-4 h-4 fill-[#0D5BD7]" strokeWidth={0} />
                </span>
                <span>Google-verified &middot; {googleReviewCount} reviews</span>
              </div>
            )}
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {(useLiveReviews ? googleReviews : TESTIMONIALS).map((t, i) => {
              const isLive = useLiveReviews;
              const author = isLive ? t.author : t.name;
              const rating = isLive ? (t.rating || 5) : t.rating;
              const text = isLive ? t.text : t.text;
              const meta = isLive ? t.time_ago : t.location;
              const photo = isLive ? t.author_photo : null;
              const truncated = text && text.length > 260 ? `${text.slice(0, 260).trim()}\u2026` : text;
              return (
                <Reveal key={author + i} delay={i * 100}>
                  <div className="h-full bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] p-8 hover:shadow-[0_18px_40px_rgba(13,91,215,0.10)] hover:-translate-y-1 transition-all duration-300 flex flex-col">
                    <div className="flex items-center gap-0.5 text-[#0D5BD7]">
                      {Array.from({ length: rating }).map((_, i2) => (
                        <Star key={i2} className="w-5 h-5 fill-[#0D5BD7]" strokeWidth={0} />
                      ))}
                    </div>
                    <p className="mt-4 text-[14.5px] text-[#5F6773] leading-[1.7] flex-1 whitespace-pre-line">&ldquo;{truncated}&rdquo;</p>
                    <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
                      {photo ? (
                        <img src={photo} alt={author} className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center font-bold text-sm">
                          {author?.[0]}
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-[#111827] text-[15px]">{author}</div>
                        <div className="text-[12px] text-[#5F6773]">{meta}</div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          {useLiveReviews && google?.maps_url && (
            <div className="mt-10 text-center">
              <a href={google.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.2em] text-[#0D5BD7] hover:underline">
                SEE ALL {googleReviewCount} REVIEWS ON GOOGLE
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F8F9FB] py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Reveal className="text-center">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">FAQ</div>
            <h2 className="mt-3 text-[32px] md:text-[44px] font-black text-[#111827] tracking-[-0.01em] leading-[1.05]">
              Frequently Asked Questions
            </h2>
          </Reveal>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 60}>
                <FaqItem
                  q={f.q}
                  a={f.a}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white pt-4 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0D5BD7] via-[#0f5cd0] to-[#0a3b8f] px-8 md:px-14 py-14 text-white">
              <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
              <div className="absolute -left-16 -bottom-20 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
              <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                <div className="max-w-xl">
                  <h3 className="text-[32px] md:text-[42px] font-black leading-[1.05] tracking-[-0.01em]">
                    Experience the A+ Difference
                  </h3>
                  <p className="mt-4 text-white/90 text-[15px] leading-[1.7]">
                    Join thousands of satisfied customers who trust A+ Auto Glass every year for premium windshield replacement, ADAS calibration and mobile service in Ottawa.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setQuoteOpen(true)}
                    className="group inline-flex items-center h-[54px] px-7 rounded-full bg-white text-[#0D5BD7] hover:bg-slate-100 text-[12px] font-bold tracking-[0.22em] transition-all shadow-md hover:-translate-y-[1px]"
                  >
                    REQUEST A QUOTE <ArrowRight className="w-4 h-4 ml-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <a
                    href="tel:3435588820"
                    className="inline-flex items-center h-[54px] px-7 rounded-full border-2 border-white text-white text-[12px] font-bold tracking-[0.22em] hover:bg-white hover:text-[#0D5BD7] transition-colors"
                  >
                    <Phone className="w-4 h-4 mr-2" /> CALL NOW
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />

      {/* Modals */}
      <QuoteRequestModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />

      {videoOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <div className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoOpen(false)} className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors">&#10005;</button>
            <div className="absolute inset-0 flex items-center justify-center text-white text-center px-6">
              <div>
                <div className="text-xs font-bold tracking-[0.24em] text-[#7fb0ff]">A+ AUTO GLASS</div>
                <div className="mt-2 text-xl font-bold">Video coming soon</div>
                <div className="mt-1 text-sm text-white/70 max-w-md">Upload a YouTube or MP4 through the admin panel to feature it here.</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
